import { useLogoutMutation } from "../api/auth";
import { useAuthSelector } from "../store/auth";
import { resetAllStores } from "../store/middleware/resetMiddleware";
import { authTokenService, clearAuthToken } from "../utilities/authTokenService";
import { clearAppCookies } from "../utilities/cookiesService";
import { enqueueSnackbar } from "notistack";
import { enqueueUniqueSnackbar } from "../utilities/snackbarUtils";
import { useQueryClient } from "@tanstack/react-query";
import { handleError } from "../utilities/errors";

/**
 * Hook to log out the current user.
 */
export function useLogout() {
  const { accessToken } = authTokenService();
  const { user } = useAuthSelector();

  const { mutateAsync: logoutMutation, error, isError, isPending } = useLogoutMutation();
  const queryClient = useQueryClient();

  // If there's a mutation error, we normalize it.
  // We keep the "throw" pattern if the user wants it to hit an Error Boundary,
  // but we ensure it's a structured AppError.
  if (isError && error) {
    throw handleError(error);
  }

  /**
   * Call server logout and cleanup user auth tokens, cookies and state.
   * after successful login user will redirect to login page.
   * **/
  async function logout(expired?: boolean, redirectTo?: string) {
    // 0. Call backend logout endpoint FIRST to invalidate server session
    // Skip if we know the session is already expired
    if (accessToken && !expired) {
      try {
        await logoutMutation(undefined);

        clearAuthStates();
        redirectToNextURL(redirectTo);
      } catch (err: unknown) {
        const appError = handleError(err);
        // If the error is just telling us we're already expired, skip the toast
        if (appError.message !== "Authentication expired") {
          enqueueUniqueSnackbar(enqueueSnackbar, appError.message || "Unable to logout from the server.", {
            variant: "error"
          });
        }
        console.warn("LOGOUT: Backend logout failed:", appError.message);
      }
    } else {
      clearAuthStates();
      redirectToNextURL(redirectTo);
    }

    // function to clear localStorage state,
    // cookies, zustand store and query cache
    function clearAuthStates() {
      // 1. Clear ALL registered stores using the global cleanup system
      resetAllStores();

      // 2. Clear all localStorage comprehensively
      // This includes: user, accessToken, refreshToken, admin settings, signed URLs, etc.
      localStorage.clear();

      // Also clear any localStorage if used (future-proofing)
      try {
        // Clear specific localStorage items that might exist
        localStorage.removeItem("tokenData"); // From Fulcrum integration
      } catch (error) {
        console.warn("⚠ LOGOUT: Error clearing localStorage:", error);
      }

      // 3. Clear app cookies
      clearAppCookies();

      // 4. Clear one time decrypted tokens from `authTokenService`
      clearAuthToken();

      localStorage.clear();
      queryClient.clear();
    }

    // `redirectTo` lets callers override the post-logout destination.
    // When omitted, we redirect to the normal redirect-aware login URL (includes `?redirect=...`).
    function redirectToNextURL(redirectTo?: string) {
      const redirectURL = redirectTo ?? loginRedirectURL();
      if (window.location.pathname !== "/login") {
        window.location.replace(redirectURL);
      }
    }
  }

  return { logout, isPending, isError, error } as const;
}

export function loginRedirectURL() {
  const { pathname, search } = window.location;
  const url = new URL("/login", window.location.origin);
  const redirectTo = `${pathname}${search}`;
  url.searchParams.set("redirect", redirectTo);

  return `${url.pathname}${url.search}`;
}
